import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { BxButtonGroupComponent, BxButtonGroupItemComponent } from '@beyonnex/bx-angular-ui';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonTranslateService } from '../../../../services/common-translate/common-translate.service';
import type { Product } from '../../../../services/property-status/property-status.service.models';
import { PRODUCT_COLUMNS } from '../property-overview.models';

@Component({
  selector: 'bxi-product-filter',
  imports: [BxButtonGroupComponent, BxButtonGroupItemComponent, TranslatePipe],
  templateUrl: './product-filter.component.html',
  host: { class: 'bxi-filter-bar block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductFilterComponent {
  private readonly commonTranslate = inject(CommonTranslateService);

  readonly selectionChange = output<Product[]>();

  protected readonly viewAllId = VIEW_ALL_ID;

  protected readonly products = PRODUCT_COLUMNS.map((product) => ({
    id: product,
    label: this.commonTranslate.translateProductAbbreviation(product),
  }));

  private readonly selection = signal<Product[]>([]);

  /** Showing no selection as "View all" keeps exactly one thing lit at all times. */
  protected readonly selectedIds = computed<string[]>(() => (this.selection().length ? this.selection() : [VIEW_ALL_ID]));

  protected handleSelectionChange(ids: string[]): void {
    const previous = this.selection();
    // The group emits the full selection, so "View all" is in the payload of any click made while it is lit —
    // only a click made while a product was selected can mean the user actually chose it.
    const products = previous.length && ids.includes(VIEW_ALL_ID) ? [] : PRODUCT_COLUMNS.filter((product) => ids.includes(product));

    // Re-seating the group even when nothing changed is what snaps the bar back to "View all".
    this.selection.set(products);
    if (products.length !== previous.length || products.some((product, index) => product !== previous[index])) {
      this.selectionChange.emit(products);
    }
  }
}

/** Not a product: the group needs an id per item, and this one cannot collide with `PRODUCT`. */
const VIEW_ALL_ID = 'VIEW_ALL';
